import styled from 'styled-components';
import { kadryMoney, type KadryOverview, type KadryOverviewBudget } from '@entities/kadry';
import { Hint, Panel, PanelHead, pct, toneColor } from './ui';

/* Каналы: доставка предложений и где кандидаты ответили; бюджеты площадок
   на месяц. */

const CHANNEL_TITLES = {
  hh: 'hh.ru',
  rabota: 'Работа.ру',
  telegram: 'Telegram',
  max: 'MAX',
  sms: 'SMS',
} as const;

/** Что оплачивается из бюджета площадки. */
const PAID_WHAT = {
  hh: 'приглашения из поиска',
  rabota: 'открытие контактов после «Подходит»',
} as const;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ChannelRow = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
`;

const Name = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Stat = styled.span<{ $tone?: 'success' | 'warning' | 'danger' }>`
  & > strong {
    margin-right: 4px;
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  }
`;

const Bar = styled.div`
  position: relative;
  height: 10px;
  margin-top: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
`;

const Seg = styled.div<{ $from: number; $share: number; $reserved?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $from }) => `${$from * 100}%`};
  width: ${({ $share }) => `${$share * 100}%`};
  background: ${({ theme, $reserved }) =>
    $reserved ? theme.colors.status.warning : theme.colors.primary};
  opacity: ${({ $reserved }) => ($reserved ? 0.6 : 1)};
`;

const BudgetLine = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Divider = styled.hr`
  margin: ${({ theme }) => `${theme.spacing(5)} 0 ${theme.spacing(4)}`};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const monthFmt = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' });

function monthTitle(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return y && m ? monthFmt.format(new Date(y, m - 1, 1)) : ym;
}

function Budget({ b }: { b: KadryOverviewBudget }) {
  const rub = (kop: number) => kadryMoney(kop / 100);
  const share = (kop: number) => (b.limit_kop > 0 ? Math.min(kop / b.limit_kop, 1) : 0);
  const spent = share(b.spent_kop);
  const reserved = Math.min(share(b.reserved_kop), 1 - spent);
  return (
    <>
      <PanelHead>
        <h3>Бюджет {CHANNEL_TITLES[b.source]}</h3>
        <span>{monthTitle(b.month)}</span>
      </PanelHead>
      {b.set ? (
        <>
          <BudgetLine>
            Потрачено <strong>{rub(b.spent_kop)}</strong> из {rub(b.limit_kop)} (
            {pct(b.spent_kop, b.limit_kop)}), в резерве {rub(b.reserved_kop)}
          </BudgetLine>
          <Bar aria-hidden>
            <Seg $from={0} $share={spent} />
            <Seg $from={spent} $share={reserved} $reserved />
          </Bar>
        </>
      ) : (
        <Hint>
          Бюджет на месяц не задан — платные действия ({PAID_WHAT[b.source]}) будут
          отклоняться. Задаёт администратор подбора в настройках.
        </Hint>
      )}
    </>
  );
}

export function ChannelsPanel({ o, delay = 0 }: { o: KadryOverview; delay?: number }) {
  const inBot = {
    telegram: o.responses.by_channel.telegram,
    max: o.responses.by_channel.max,
  };
  return (
    <Panel $delay={delay}>
      <PanelHead>
        <h3>Каналы</h3>
        <span>доставка предложений и ответы</span>
      </PanelHead>
      <Rows>
        {o.deliveries.map((d) => (
          <ChannelRow key={d.channel}>
            <Name>{CHANNEL_TITLES[d.channel]}</Name>
            <Stats>
              <Stat $tone="success">
                <strong>{d.sent}</strong>доставлено
              </Stat>
              {d.failed > 0 && (
                <Stat $tone="danger">
                  <strong>{d.failed}</strong>ошибки
                </Stat>
              )}
              {d.unknown + d.queued > 0 && (
                <Stat $tone="warning">
                  <strong>{d.unknown + d.queued}</strong>в пути
                </Stat>
              )}
              {d.skipped > 0 && (
                <Stat>
                  <strong>{d.skipped}</strong>не использован
                </Stat>
              )}
              {(d.channel === 'telegram' || d.channel === 'max') && (
                <Stat>
                  <strong>{inBot[d.channel]}</strong>ответили в боте
                </Stat>
              )}
            </Stats>
          </ChannelRow>
        ))}
      </Rows>
      <Hint>
        По ссылкам из приглашений hh.ru и Работа.ру ответили: {o.responses.by_channel.web}. Бот
        пишет только тем, кто открыл его по ссылке из приглашения, — поэтому «не использован» у
        мессенджеров — обычное дело для новых кандидатов.
        {o.responses.by_channel.platform > 0 &&
          ` Сами откликнулись на вакансии и подошли: ${o.responses.by_channel.platform}.`}
      </Hint>
      {o.budgets.map((b) => (
        <div key={b.source}>
          <Divider />
          <Budget b={b} />
        </div>
      ))}
    </Panel>
  );
}
